import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const CONTACT_METHODS = [
  { key: 'EMAIL', label: 'Email', icon: 'mail', placeholder: 'relative@example.com' },
  { key: 'PHONE', label: 'Phone (SMS)', icon: 'phone', placeholder: '+1 (555) 123-4567' },
];

const PERMISSION_LEVELS = [
  { key: 'VIEWER', label: 'Viewer', desc: 'Can view family tree and memories', icon: 'viewer' },
  { key: 'CONTRIBUTOR', label: 'Contributor', desc: 'Can add memories and update their own profile', icon: 'contributor' },
  { key: 'ADMIN', label: 'Admin', desc: 'Full access to manage members and settings', icon: 'admin' },
];

const RELATIONSHIP_EXAMPLES = [
  '"We are cousins through my mother\'s side"',
  '"We knew each other from church in the 1980s"',
  '"My father is your uncle"',
  '"We met at the family reunion in 2022"',
];

function SendInvitationPage() {
  const navigate = useNavigate();
  const [contactMethod, setContactMethod] = useState('EMAIL');
  const [contactValue, setContactValue] = useState('');
  const [relationshipHint, setRelationshipHint] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('VIEWER');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);

  const validate = () => {
    const newErrors = {};
    if (!contactValue.trim()) {
      newErrors.contact = contactMethod === 'EMAIL' ? 'Email address is required' : 'Phone number is required';
    } else if (contactMethod === 'EMAIL') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactValue)) newErrors.contact = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // TODO: GraphQL mutation
    setTimeout(() => {
      setSubmitting(false);
      setSent({
        method: contactMethod,
        contact: contactValue,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      });
    }, 1500);
  };

  const handleReset = () => {
    setContactValue('');
    setRelationshipHint('');
    setPermissionLevel('VIEWER');
    setSent(null);
    setErrors({});
  };

  if (sent) {
    return (
      <div className="flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AppIcon name="inbox" size={60} />
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Invitation Sent!</h2>
          <p className="mt-2 text-gray-600">
            Your invitation has been sent to <strong>{sent.contact}</strong>
          </p>
          <div className="mt-4 bg-gray-50 rounded-lg p-4 text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Method</span>
                <span className="font-medium">{sent.method === 'EMAIL' ? <> <AppIcon name="email" size={14} /> Email</> : <> <AppIcon name="phone" size={14} /> SMS</>}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Expires</span>
                <span className="font-medium">{sent.expiresAt}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Permission</span>
                <span className="font-medium capitalize">{permissionLevel.toLowerCase()}</span>
              </div>
            </div>
          </div>
          <div className="mt-6 flex gap-3 justify-center">
            <button onClick={() => navigate('/invitations')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              View Invitations
            </button>
            <button onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              Send Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentMethod = CONTACT_METHODS.find((m) => m.key === contactMethod);
  const currentPermission = PERMISSION_LEVELS.find((p) => p.key === permissionLevel);

  return (
    <div className="">

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">How should we reach them?</label>
            <div className="grid grid-cols-2 gap-3">
              {CONTACT_METHODS.map((method) => (
                <button
                  key={method.key}
                  type="button"
                  onClick={() => { setContactMethod(method.key); setContactValue(''); setErrors({}); }}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    contactMethod === method.key
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <AppIcon name={method.icon} size={24} />
                  <span className="text-sm font-medium">{method.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Contact Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {contactMethod === 'EMAIL' ? 'Email Address' : 'Phone Number'} *
            </label>
            <input
              type={contactMethod === 'EMAIL' ? 'email' : 'tel'}
              value={contactValue}
              onChange={(e) => { setContactValue(e.target.value); setErrors({}); }}
              placeholder={currentMethod?.placeholder}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.contact ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.contact && <p className="text-xs text-red-600 mt-1">{errors.contact}</p>}
          </div>

          {/* Relationship Hint */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">How are you related?</label>
            <textarea
              value={relationshipHint}
              onChange={(e) => setRelationshipHint(e.target.value)}
              placeholder="Help us understand your relationship..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {RELATIONSHIP_EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setRelationshipHint(ex)}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          {/* Permission Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">What can they do?</label>
            <div className="space-y-3">
              {PERMISSION_LEVELS.map((level) => (
                <button
                  key={level.key}
                  type="button"
                  onClick={() => setPermissionLevel(level.key)}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    permissionLevel === level.key
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <AppIcon name={level.icon} size={24} className="mt-0.5" />
                  <div>
                    <p className={`text-sm font-semibold ${permissionLevel === level.key ? 'text-indigo-700' : 'text-gray-900'}`}>
                      {level.label}
                    </p>
                    <p className="text-xs text-gray-500">{level.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Invitation Preview</h3>
            <p className="text-sm text-gray-600">
              You're inviting <strong>{contactValue || 'someone'}</strong> via <strong>{contactMethod === 'EMAIL' ? 'email' : 'SMS'}</strong> to join your family on Mien Family as a <strong className="capitalize">{currentPermission?.label.toLowerCase()}</strong>.
              {relationshipHint && (
                <span className="block mt-1 text-xs text-gray-500 italic">"{relationshipHint}"</span>
              )}
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Invitation'
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default SendInvitationPage;